/* Small portal UI primitives ported from the Claude design. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Ico } from './Icons';
import type { PortfolioStatus } from '@/portal/api/types';

export function Avatar({ initials, sm }: { initials: string; sm?: boolean }) {
  return <div className={'pl-avatar' + (sm ? ' sm' : '')}>{initials}</div>;
}

export function Pill({ kind, children }: { kind: string; children: ReactNode }) {
  return (
    <span className={'pill ' + kind}>
      <span className="dot" />
      {children}
    </span>
  );
}

const STATUS_LABEL: Record<string, string> = {
  live: 'Live',
  draft: 'Draft',
  archived: 'Archived',
  shared: 'Shared',
  sent: 'Sent',
};

export function statusPill(status: PortfolioStatus | string) {
  return <Pill kind={status}>{STATUS_LABEL[status] ?? status}</Pill>;
}

const DRAWER_WIDTH_KEY = 'pl:drawer-width';
const DRAWER_MIN = 380;
const DRAWER_DEFAULT = 640;
const drawerMax = () => Math.round(window.innerWidth * 0.94);
const clampDrawer = (w: number) => Math.min(drawerMax(), Math.max(DRAWER_MIN, w));

export function Drawer({
  title,
  onClose,
  footer,
  stickyBar,
  children,
}: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  stickyBar?: ReactNode;
  children: ReactNode;
}) {
  const [width, setWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(DRAWER_WIDTH_KEY));
    return saved >= DRAWER_MIN ? clampDrawer(saved) : DRAWER_DEFAULT;
  });
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      // Drawer is right-anchored, so width grows as the cursor moves left.
      const w = clampDrawer(window.innerWidth - ev.clientX);
      widthRef.current = w;
      setWidth(w);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem(DRAWER_WIDTH_KEY, String(Math.round(widthRef.current)));
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  return (
    <>
      <div className="pl-drawer-scrim" onClick={onClose} />
      <div className="pl-drawer" role="dialog" aria-label={title} style={{ width }}>
        <div
          className="pl-drawer-resize"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          title="Drag to resize"
        />
        <div className="pl-drawer-head">
          <h3>{title}</h3>
          <button className="pl-x" onClick={onClose} aria-label="Close">
            <Ico.x width={20} height={20} />
          </button>
        </div>
        {stickyBar && <div className="pl-drawer-sticky">{stickyBar}</div>}
        <div className="pl-drawer-body">{children}</div>
        {footer && <div className="pl-drawer-foot">{footer}</div>}
      </div>
    </>
  );
}
